
import { useQuery } from 'react-query';
import { useAtom } from "jotai";
import React, { memo, useEffect, useRef, useState } from 'react';
import { Radio, Checkbox } from 'antd';
import styled from "styled-components";
import { CaretDownOutlined, CaretUpOutlined } from '@ant-design/icons';
import { fetchStopSalesYears } from '@Services/article';
import { IFilterParams } from '@Types/specification';
import { initTracking } from "@Lib/jotai/tracking";


const RadioStyled = styled(Radio.Group)`
 .ant-radio-wrapper{
    margin-right: 6px;
  }
  .ant-radio-checked .ant-radio-inner{
    border-color: #3264FF;
  }
  .ant-radio-inner::after{
    background-color: #3264FF;
  }
`;

const CheckboxStyled = styled(Checkbox.Group)`
  .ant-checkbox-wrapper{
    &:first-child{  
      margin-left: 0px;
    }
  }
  .ant-checkbox-wrapper{
    margin-left: 6px;
  }
  .ant-checkbox-checked .ant-checkbox-inner {
    background-color: #3264FF;
    border-color: #3264FF;
  } .ant-checkbox-checked .ant-checkbox-inner
`;

interface IProps {
  updateParams: (params: IParams) => void;
  updateFilterCheckbox: (params: { gas: string[], power: string[], driveType: string[] }) => void;
  filterParams: IFilterParams;
}

export interface IParams {
  allSalesRadio: string | null; // 全部在售
  isSalesCheckbox: string[]; // 年式nk多选
  stopSale: string; // 停售对应的nk
  inStock?: string | null; // 1为在售，-1为停售
}

const initialParams = {
  allSalesRadio: '1',
  isSalesCheckbox: [],
  stopSale: "",
  inStock: '1',
};

const Filter: React.FC<IProps> = ({ updateParams, filterParams, updateFilterCheckbox }) => {
  const dropdownRef = useRef(null);
  const [tracking] = useAtom(initTracking);
  const [params, setParams] = useState<IParams>(initialParams);
  const [stopSaleIndex, setStopSaleIndex] = useState(-1); // 停售选中的索引
  const [stopSaleModalVisible, setStopSaleModalVisible] = useState(false); // 停售下拉框显示
  const [driveTypeCheckbox, setDriveTypeCheckbox] = useState<string[]>([]); // 驅動型式多選
  const [powerCheckbox, setPowerCheckbox] = useState<string[]>([]); // 动力型式多选
  const [gasCheckbox, setGasCheckbox] = useState<string[]>([]); // 排氣量多选

  const { data = { new: [], old: [] } } = useQuery<any>(
    [`fetchStopSalesYears`, window.query_kind_id],
    async () => {
      const res: { status; data } = await fetchStopSalesYears({ k: window.query_kind_id });
      if (res?.status === 200) {

        // 从综述页进来
        if (!!window.NK) {
          const stopFindIndex = res.data.old.findIndex((item) => item.new_kind_id == (window.NK || ''));
          if (stopFindIndex !== -1) {
            setStopSaleIndex(stopFindIndex);
            setParams((prev) => ({ ...prev, inStock: '-1', stopSale: res.data.old[stopFindIndex].new_kind_id }));
          }
        } else if (!res.data.new.length) {
          // 非综述页进来，且没有在售年款
          setStopSaleIndex(0);
          setParams((prev) => ({ ...prev, inStock: '-1', stopSale: res.data.old[0].new_kind_id }));
        }

        return res.data;
      }
      return { new: [], old: [] };
    },
  );

  // 全部在售
  const handleRadioChange = (e) => {
    const value = e.target.value;
    setParams((prev) => ({
      ...prev,
      allSalesRadio: value,
      isSalesCheckbox: [],
      inStock: '1'
    }));
    setStopSaleIndex(-1);
    handleYearsExposured();
  };

  // 年式多选框
  const handleCheckboxChange = (checkedValues) => {
    setParams((prev) => ({
      ...prev,
      isSalesCheckbox: checkedValues,
      inStock: '1',
      allSalesRadio: !!checkedValues.length ? null : '1'
    }));
    setStopSaleIndex(-1);
    handleYearsExposured();
  };

  // 停售下拉框
  const handleStopSaleSelect = (index) => {
    setStopSaleIndex(index);
    setParams((prev) => ({
      ...prev,
      allSalesRadio: null,
      isSalesCheckbox: [],
      stopSale: data.old[index].new_kind_id,
      inStock: index !== -1 ? '-1' : null,
    }));
    setStopSaleModalVisible(false);
    handleYearsExposured();
  };

  // 排气量
  const handleGasCheckboxChange = (checkedValues) => {
    setGasCheckbox(checkedValues);
    updateFilterCheckbox({ gas: checkedValues, power: powerCheckbox, driveType: driveTypeCheckbox });
  };

  // 动力型式
  const handlePowerCheckboxChange = (checkedValues) => {
    setPowerCheckbox(checkedValues);
    updateFilterCheckbox({ power: checkedValues, gas: gasCheckbox, driveType: driveTypeCheckbox });
  };

  // 驅動型式
  const handleDriveCheckboxChange = (checkedValues) => {
    setDriveTypeCheckbox(checkedValues);
    updateFilterCheckbox({ driveType: checkedValues, gas: gasCheckbox, power: powerCheckbox });
  };

  const handleYearsExposured = () => {
    tracking && tracking.trackEvent({
      name: "element_clicked",
      params: {
        page_sub_business: "新車資料庫",
        page_business: "新車",
        trigger_id: "3747",
        page_id: "266",
        trigger_name: "年款篩選條件",
        next_spm: "3.266.691.1918",
        page: "規格配備頁",
        channel: "新車",
        element_id: "年款篩選條件",
        element: "按鈕",
        module: "規格配備篩選條件",
        tracker_method: "config"
      },
    });
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !(dropdownRef.current as HTMLElement).contains(event.target as HTMLElement)) {
        setStopSaleModalVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    updateParams(params);
  }, [params]);

  useEffect(() => {
    setDriveTypeCheckbox([])
    setPowerCheckbox([])
    setGasCheckbox([])
  }, [filterParams]);

  return (
    <section className={`flex items-center bg-[#fff] sticky left-[180px] z-[3] min-w-0 w-fit`}>
      <div className='pt-12 pb-4 bg-white rounded-tl-4 rounded-tr-4 flex items-center justify-start gap-40 pl-14 w-max'>
        <div className='flex items-center' id="tracking-YearCheckbox">
          <span className='text-14 text-[#656873] mr-14'>年式</span>
          {Array.isArray(data.new) && !!data.new.length && <>
            <RadioStyled onChange={handleRadioChange} value={stopSaleIndex === -1 ? params.allSalesRadio || null : null}>
              <Radio value="1">全部在售</Radio>
            </RadioStyled>

            <CheckboxStyled value={params.isSalesCheckbox} onChange={handleCheckboxChange}>
              <div className='flex items-center'>
                {data.new.map((item) => (
                  <Checkbox key={item} value={item.new_kind_id}>{item.year}年式</Checkbox>
                ))}
                {!!data.new.length && !!data.old.length && <span className='w-1 h-16 bg-[#9DA2AF] ml-6 mr-14'></span>}
              </div>
            </CheckboxStyled>
          </>}

          {Array.isArray(data.old) && !!data.old.length && <div className={`text-14 text-[${stopSaleIndex !== -1 ? '#3264FF' : '#1D2029'}] cursor-pointer relative`} ref={dropdownRef}>
            <span onClick={() => setStopSaleModalVisible(!stopSaleModalVisible)}>
              {stopSaleIndex !== -1 && (!!data.old[stopSaleIndex].year ? data.old[stopSaleIndex].year + '年式' : '')} 停售
              {stopSaleModalVisible ? <CaretUpOutlined style={{ fontSize: '12px', color: '#666666', marginLeft: '8px' }} /> : <CaretDownOutlined style={{ fontSize: '12px', color: '#666666', marginLeft: '8px' }} />}
            </span>

            {stopSaleModalVisible && !!data.old.length && (
              <div className='absolute top-30 -left-14 w-104 flex flex-col items-center bg-white rounded-4 py-4' style={{ boxShadow: '0px 2px 18px 0px rgba(0,0,0,0.20)' }}>
                {data.old.map((item, index) => (
                  <span
                    className={`text-14 text-[${stopSaleIndex === index ? '#3264FF' : '#1D2029'}] h-32 w-96 flex items-center justify-start pl-12 hover:bg-[#E0EFFF] rounded-4`}
                    key={index}
                    style={{ background: stopSaleIndex === index ? '#D8EDFF' : '' }}
                    onClick={() => handleStopSaleSelect(index)}
                  >
                    {!!item.year ? item.year + '年式' : ''}
                  </span>
                ))}
              </div>
            )}
          </div>}
        </div>

        {Array.isArray(filterParams.gas) && !!filterParams.gas.length && filterParams.gas.length > 1 && <div className='flex items-center gap-14'>
          <span className='text-14 text-[#656873]'>排氣量</span>
          <CheckboxStyled value={gasCheckbox} onChange={handleGasCheckboxChange}>
            <div className='flex items-center' id="tracking-GasCheckbox">
              {filterParams.gas.map((item) => (
                <Checkbox key={item} value={item}>{item}L</Checkbox>
              ))}
            </div>
          </CheckboxStyled>
        </div>}

        {Array.isArray(filterParams.power) && !!filterParams.power.length && filterParams.power.length > 1 && <div className='flex items-center gap-14'>
          <span className='text-14 text-[#656873]'>動力型式</span>
          <CheckboxStyled value={powerCheckbox} onChange={handlePowerCheckboxChange}>
            <div className='flex items-center' id="tracking-PowerCheckbox">
              {filterParams.power.map((item) => (
                <Checkbox key={item} value={item}>{item}</Checkbox>
              ))}
            </div>
          </CheckboxStyled>
        </div>}

        {Array.isArray(filterParams.driveType) && !!filterParams.driveType.length && filterParams.driveType.length > 1 && <div className='flex items-center gap-14'>
          <span className='text-14 text-[#656873]'>驅動型式</span>
          <CheckboxStyled value={driveTypeCheckbox} onChange={handleDriveCheckboxChange}>
            <div className='flex items-center' id="tracking-DriveCheckbox">
              {filterParams.driveType.map((item) => (
                <Checkbox key={item} value={item}>{item}</Checkbox>
              ))}
            </div>
          </CheckboxStyled>
        </div>}
      </div>

    </section>

  );
};

export default memo(Filter);
